import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import DataTable from '../components/DataTable';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import { getClients } from '../api/clientsApi';
import { useToast } from '../context/ToastContext';

function Clients() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchClients = async (pageNum = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const result = await getClients(pageNum, 10);
      if (result.success) {
        setClients((prev) => (append ? [...prev, ...result.clients] : result.clients));
        setHasMore(result.has_more);
        setPage(pageNum);
      } else {
        setError(result.message || 'Failed to load clients.');
      }
    } catch (err) {
      setError('Could not connect to server.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchClients(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      accessorKey: 'name',
      header: 'Company',
      cell: (info) => (
        <span
          style={{
            color: '#2563eb',
            cursor: 'pointer',
            fontWeight: 700,
            opacity: info.row.original.is_active ? 1 : 0.5,
          }}
          onClick={() => navigate(`/clients/${info.row.original.id_client}`)}
        >
          {info.getValue()}
        </span>
      ),
    },
    { accessorKey: 'contact_name', header: 'Contact', cell: (info) => info.getValue() || '—' },
    { accessorKey: 'email', header: 'Email', cell: (info) => info.getValue() || '—' },
    { accessorKey: 'phone', header: 'Phone', cell: (info) => info.getValue() || '—' },
    { accessorKey: 'load_count', header: 'Loads', cell: (info) => info.getValue() },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: (info) => (
        <span
          style={{
            padding: '3px 10px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            backgroundColor: info.getValue() ? '#d1fae5' : '#fee2e2',
            color: info.getValue() ? '#065f46' : '#991b1b',
          }}
        >
          {info.getValue() ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar
          title="Clients"
          rightAction={
            <button onClick={() => navigate('/clients/create')} style={styles.createBtn}>
              <Plus size={16} style={{ marginRight: 6 }} />
              Add Client
            </button>
          }
        />

        <div style={{ padding: 24 }}>
          {loading && <LoadingSpinner fullScreen />}

          {!loading && error && (
            <EmptyState
              icon={Building2}
              title="Something went wrong"
              message={error}
              onRetry={() => fetchClients(1)}
              retryLabel="Try again"
            />
          )}

          {!loading && !error && clients.length === 0 && (
            <EmptyState
              icon={Building2}
              title="No clients yet"
              message="Add your first client company to get started."
            />
          )}

          {!loading && !error && clients.length > 0 && (
            <>
              <DataTable columns={columns} data={clients} />
              {hasMore && (
                <button
                  onClick={() => fetchClients(page + 1, true)}
                  disabled={loadingMore}
                  style={styles.loadMoreBtn}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  createBtn: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  loadMoreBtn: {
    width: '100%',
    padding: '12px 0',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    color: '#2563eb',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    marginTop: 12,
  },
};

export default Clients;